import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Package, Warehouse, ArrowRightLeft, ClipboardList, AlertTriangle,
  Plus, Search, Filter, MoreVertical, RefreshCw, TrendingDown, TrendingUp,
  Box, DollarSign, BarChart3, ArrowUpRight, ArrowDownRight, Edit, Trash2, Eye
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth-context';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LOCATION_TYPES = [
  { value: 'WAREHOUSE', label: 'Warehouse' },
  { value: 'STORE', label: 'Store Room' },
  { value: 'PANTRY', label: 'Floor Pantry' },
  { value: 'KITCHEN', label: 'Kitchen' },
  { value: 'BAR', label: 'Bar' },
  { value: 'LAUNDRY', label: 'Laundry' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
];

const MOVEMENT_TYPES = [
  { value: 'RECEIPT', label: 'Goods Receipt', icon: ArrowDownRight, color: 'text-green-600' },
  { value: 'ISSUE', label: 'Stock Issue', icon: ArrowUpRight, color: 'text-red-600' },
  { value: 'TRANSFER', label: 'Transfer', icon: ArrowRightLeft, color: 'text-blue-600' },
  { value: 'ADJUSTMENT', label: 'Adjustment', icon: Edit, color: 'text-amber-600' },
];

export default function InventoryPage() {
  const { currentProperty } = useAuth();
  const propertyRef = useRef(currentProperty?.id);
  propertyRef.current = currentProperty?.id;
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [movements, setMovements] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [selectedMovementType, setSelectedMovementType] = useState('RECEIPT');
  
  // Form states
  const [itemForm, setItemForm] = useState({
    sku: '', name: '', description: '', category_id: '', unit_of_measure: 'EA',
    unit_cost: 0, reorder_point: 0, reorder_quantity: 0, max_stock_level: 0,
    is_perishable: false, shelf_life_days: null
  });
  const [categoryForm, setCategoryForm] = useState({ code: '', name: '', description: '' });
  const [locationForm, setLocationForm] = useState({ code: '', name: '', location_type: 'WAREHOUSE', is_default: false });
  const [movementForm, setMovementForm] = useState({
    movement_type: 'RECEIPT', from_location_id: '', to_location_id: '', department: '', notes: '',
    lines: [{ item_id: '', quantity: 0, unit_cost: 0 }]
  });

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('hms_token')}`,
    ...(propertyRef.current ? { 'X-Property-Id': propertyRef.current } : {}),
  });

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory/dashboard/summary`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory/dashboard/alerts`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setAlerts(data);
      }
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory/items`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (error) {
      console.error('Failed to fetch items:', error);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory/categories`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory/locations`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  }, []);

  const fetchMovements = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory/movements?limit=50`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setMovements(data);
      }
    } catch (error) {
      console.error('Failed to fetch movements:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchAlerts(),
        fetchItems(),
        fetchCategories(),
        fetchLocations(),
        fetchMovements(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [currentProperty?.id, fetchDashboard, fetchAlerts, fetchItems, fetchCategories, fetchLocations, fetchMovements]);

  const handleCreateItem = async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory/items`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(itemForm),
      });
      
      if (response.ok) {
        toast.success('Item created successfully');
        setItemDialogOpen(false);
        setItemForm({
          sku: '', name: '', description: '', category_id: '', unit_of_measure: 'EA',
          unit_cost: 0, reorder_point: 0, reorder_quantity: 0, max_stock_level: 0,
          is_perishable: false, shelf_life_days: null
        });
        fetchItems();
        fetchDashboard();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create item');
      }
    } catch (error) {
      toast.error('Failed to create item');
    }
  };

  const handleCreateCategory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory/categories`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(categoryForm),
      });
      
      if (response.ok) {
        toast.success('Category created successfully');
        setCategoryDialogOpen(false);
        setCategoryForm({ code: '', name: '', description: '' });
        fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create category');
      }
    } catch (error) {
      toast.error('Failed to create category');
    }
  };

  const handleCreateLocation = async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory/locations`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(locationForm),
      });
      
      if (response.ok) {
        toast.success('Location created successfully');
        setLocationDialogOpen(false);
        setLocationForm({ code: '', name: '', location_type: 'WAREHOUSE', is_default: false });
        fetchLocations();
        fetchDashboard();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create location');
      }
    } catch (error) {
      toast.error('Failed to create location');
    }
  };

  const handleCreateMovement = async () => {
    try {
      const payload = {
        ...movementForm,
        lines: movementForm.lines.filter(l => l.item_id && l.quantity > 0)
      };
      
      const response = await fetch(`${API_URL}/api/inventory/movements`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        const movement = await response.json();
        
        // Auto-post the movement
        await fetch(`${API_URL}/api/inventory/movements/${movement.id}/post`, {
          method: 'POST',
          headers: getAuthHeaders(),
        });
        
        toast.success('Movement created and posted successfully');
        setMovementDialogOpen(false);
        setMovementForm({
          movement_type: 'RECEIPT', from_location_id: '', to_location_id: '', department: '', notes: '',
          lines: [{ item_id: '', quantity: 0, unit_cost: 0 }]
        });
        fetchMovements();
        fetchDashboard();
        fetchAlerts();
        fetchItems();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create movement');
      }
    } catch (error) {
      toast.error('Failed to create movement');
    }
  };

  const addMovementLine = () => {
    setMovementForm(prev => ({
      ...prev,
      lines: [...prev.lines, { item_id: '', quantity: 0, unit_cost: 0 }]
    }));
  };

  const updateMovementLine = (index, field, value) => {
    setMovementForm(prev => ({
      ...prev,
      lines: prev.lines.map((line, i) => i === index ? { ...line, [field]: value } : line)
    }));
  };

  const removeMovementLine = (index) => {
    if (movementForm.lines.length > 1) {
      setMovementForm(prev => ({
        ...prev,
        lines: prev.lines.filter((_, i) => i !== index)
      }));
    }
  };

  const getStockStatusBadge = (item) => {
    if (item.total_stock <= 0) {
      return <Badge variant="destructive">Out of Stock</Badge>;
    } else if (item.total_stock <= item.reorder_point) {
      return <Badge variant="outline" className="text-amber-600 border-amber-300">Low Stock</Badge>;
    }
    return <Badge variant="outline" className="text-green-600 border-green-300">In Stock</Badge>;
  };

  const filteredItems = items.filter(item => 
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Inventory Management</h1>
          <p className="text-muted-foreground">Track and manage your inventory</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => {
            fetchDashboard();
            fetchAlerts();
            fetchItems();
            fetchMovements();
          }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => {
            setSelectedMovementType('RECEIPT');
            setMovementForm(prev => ({ ...prev, movement_type: 'RECEIPT' }));
            setMovementDialogOpen(true);
          }}>
            <Plus className="w-4 h-4 mr-2" />
            New Movement
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="items">Items</TabsTrigger>
          <TabsTrigger value="locations">Locations</TabsTrigger>
          <TabsTrigger value="movements">Movements</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Items</p>
                    <p className="text-2xl font-bold">{dashboardData?.total_items || 0}</p>
                  </div>
                  <Package className="w-8 h-8 text-primary/30" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Locations</p>
                    <p className="text-2xl font-bold">{dashboardData?.total_locations || 0}</p>
                  </div>
                  <Warehouse className="w-8 h-8 text-blue-500/30" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Stock Value</p>
                    <p className="text-2xl font-bold">${(dashboardData?.total_stock_value || 0).toLocaleString()}</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-green-500/30" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Low Stock Alerts</p>
                    <p className="text-2xl font-bold text-amber-600">{dashboardData?.low_stock_alerts || 0}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-amber-500/30" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2 text-amber-500" />
                  Stock Alerts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {alerts.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No alerts</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {alerts.slice(0, 10).map((alert, i) => (
                      <div key={i} className={`p-3 rounded-lg border ${
                        alert.severity === 'critical' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-sm">{alert.item_name}</p>
                            <p className="text-xs text-muted-foreground">{alert.message}</p>
                          </div>
                          <Badge variant={alert.severity === 'critical' ? 'destructive' : 'outline'} className="text-xs">
                            {alert.type.replace('_', ' ')}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Movements */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <ArrowRightLeft className="w-5 h-5 mr-2" />
                  Recent Movements
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboardData?.recent_movements?.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No recent movements</p>
                ) : (
                  <div className="space-y-3">
                    {dashboardData?.recent_movements?.map((mov, i) => {
                      const typeConfig = MOVEMENT_TYPES.find(t => t.value === mov.movement_type);
                      const Icon = typeConfig?.icon || ArrowRightLeft;
                      return (
                        <div key={i} className="flex items-center justify-between p-3 rounded-lg border">
                          <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-lg bg-muted ${typeConfig?.color}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{mov.movement_number}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(mov.movement_date).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge variant="outline">{mov.status}</Badge>
                            <p className="text-xs text-muted-foreground mt-1">
                              ${(mov.total_cost || 0).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Items Tab */}
        <TabsContent value="items" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button onClick={() => setItemDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>UOM</TableHead>
                  <TableHead className="text-right">Stock</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No items found. Create your first item to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.sku}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.category_name || '-'}</TableCell>
                      <TableCell>{item.unit_of_measure}</TableCell>
                      <TableCell className="text-right">{item.total_stock}</TableCell>
                      <TableCell className="text-right">${item.stock_value.toFixed(2)}</TableCell>
                      <TableCell>{getStockStatusBadge(item)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Locations Tab */}
        <TabsContent value="locations" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setLocationDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Location
            </Button>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {locations.length === 0 ? (
              <Card className="col-span-3">
                <CardContent className="text-center py-12">
                  <Warehouse className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground">No locations found. Create your first location.</p>
                </CardContent>
              </Card>
            ) : (
              locations.map((loc) => (
                <Card key={loc.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{loc.name}</CardTitle>
                      {loc.is_default && <Badge>Default</Badge>}
                    </div>
                    <CardDescription>{loc.code} • {loc.location_type}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Items:</span>
                      <span className="font-medium">{loc.item_count}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Value:</span>
                      <span className="font-medium">${loc.total_value.toLocaleString()}</span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Movements Tab */}
        <TabsContent value="movements" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {MOVEMENT_TYPES.map((type) => (
                <Button
                  key={type.value}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedMovementType(type.value);
                    setMovementForm(prev => ({ ...prev, movement_type: type.value }));
                    setMovementDialogOpen(true);
                  }}
                >
                  <type.icon className={`w-4 h-4 mr-2 ${type.color}`} />
                  {type.label}
                </Button>
              ))}
            </div>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead className="text-right">Items</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No movements found.
                    </TableCell>
                  </TableRow>
                ) : (
                  movements.map((mov) => {
                    const typeConfig = MOVEMENT_TYPES.find(t => t.value === mov.movement_type);
                    return (
                      <TableRow key={mov.id}>
                        <TableCell className="font-medium">{mov.movement_number}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={typeConfig?.color}>
                            {typeConfig?.label || mov.movement_type}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(mov.movement_date).toLocaleDateString()}</TableCell>
                        <TableCell>{mov.from_location_name || '-'}</TableCell>
                        <TableCell>{mov.to_location_name || mov.department || '-'}</TableCell>
                        <TableCell className="text-right">{mov.total_items}</TableCell>
                        <TableCell className="text-right">${mov.total_cost.toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge variant={mov.status === 'POSTED' ? 'default' : 'outline'}>
                            {mov.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setCategoryDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            {categories.length === 0 ? (
              <Card className="col-span-4">
                <CardContent className="text-center py-12">
                  <Box className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground">No categories found. Create your first category.</p>
                </CardContent>
              </Card>
            ) : (
              categories.map((cat) => (
                <Card key={cat.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{cat.name}</CardTitle>
                    <CardDescription>{cat.code}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{cat.item_count} items</p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Item Dialog */}
      <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>SKU *</Label>
                <Input
                  value={itemForm.sku}
                  onChange={(e) => setItemForm(prev => ({ ...prev, sku: e.target.value }))}
                  placeholder="ITEM001"
                />
              </div>
              <div>
                <Label>Unit of Measure</Label>
                <Select value={itemForm.unit_of_measure} onValueChange={(v) => setItemForm(prev => ({ ...prev, unit_of_measure: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EA">Each (EA)</SelectItem>
                    <SelectItem value="CS">Case (CS)</SelectItem>
                    <SelectItem value="KG">Kilogram (KG)</SelectItem>
                    <SelectItem value="L">Liter (L)</SelectItem>
                    <SelectItem value="M">Meter (M)</SelectItem>
                    <SelectItem value="PK">Pack (PK)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Name *</Label>
              <Input
                value={itemForm.name}
                onChange={(e) => setItemForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Item name"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={itemForm.category_id} onValueChange={(v) => setItemForm(prev => ({ ...prev, category_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Standard Cost</Label>
                <Input
                  type="number"
                  value={itemForm.unit_cost}
                  onChange={(e) => setItemForm(prev => ({ ...prev, unit_cost: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Reorder Point</Label>
                <Input
                  type="number"
                  value={itemForm.reorder_point}
                  onChange={(e) => setItemForm(prev => ({ ...prev, reorder_point: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Max Stock</Label>
                <Input
                  type="number"
                  value={itemForm.max_stock_level}
                  onChange={(e) => setItemForm(prev => ({ ...prev, max_stock_level: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={itemForm.description}
                onChange={(e) => setItemForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateItem} disabled={!itemForm.sku || !itemForm.name}>Create Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Code *</Label>
              <Input
                value={categoryForm.code}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, code: e.target.value }))}
                placeholder="CAT001"
              />
            </div>
            <div>
              <Label>Name *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Category name"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCategory} disabled={!categoryForm.code || !categoryForm.name}>Create Category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Location Dialog */}
      <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Location</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Code *</Label>
              <Input
                value={locationForm.code}
                onChange={(e) => setLocationForm(prev => ({ ...prev, code: e.target.value }))}
                placeholder="WH001"
              />
            </div>
            <div>
              <Label>Name *</Label>
              <Input
                value={locationForm.name}
                onChange={(e) => setLocationForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Main Warehouse"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={locationForm.location_type} onValueChange={(v) => setLocationForm(prev => ({ ...prev, location_type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOCATION_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLocationDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateLocation} disabled={!locationForm.code || !locationForm.name}>Create Location</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement Dialog */}
      <Dialog open={movementDialogOpen} onOpenChange={setMovementDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {MOVEMENT_TYPES.find(t => t.value === movementForm.movement_type)?.label || 'New Movement'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {(movementForm.movement_type === 'ISSUE' || movementForm.movement_type === 'TRANSFER') && (
                <div>
                  <Label>From Location *</Label>
                  <Select value={movementForm.from_location_id} onValueChange={(v) => setMovementForm(prev => ({ ...prev, from_location_id: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {(movementForm.movement_type === 'RECEIPT' || movementForm.movement_type === 'TRANSFER' || movementForm.movement_type === 'ADJUSTMENT') && (
                <div>
                  <Label>To Location *</Label>
                  <Select value={movementForm.to_location_id} onValueChange={(v) => setMovementForm(prev => ({ ...prev, to_location_id: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {movementForm.movement_type === 'ISSUE' && (
                <div>
                  <Label>Department</Label>
                  <Select value={movementForm.department} onValueChange={(v) => setMovementForm(prev => ({ ...prev, department: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Housekeeping">Housekeeping</SelectItem>
                      <SelectItem value="F&B">F&B</SelectItem>
                      <SelectItem value="Maintenance">Maintenance</SelectItem>
                      <SelectItem value="Front Office">Front Office</SelectItem>
                      <SelectItem value="Spa">Spa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Items</Label>
                <Button type="button" variant="ghost" size="sm" onClick={addMovementLine}>
                  <Plus className="w-4 h-4 mr-1" /> Add Line
                </Button>
              </div>
              <div className="space-y-2">
                {movementForm.lines.map((line, index) => (
                  <div key={index} className="flex gap-2 items-end">
                    <div className="flex-1">
                      {index === 0 && <Label className="text-xs text-muted-foreground">Item</Label>}
                      <Select value={line.item_id} onValueChange={(v) => updateMovementLine(index, 'item_id', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select item" />
                        </SelectTrigger>
                        <SelectContent>
                          {items.map((item) => (
                            <SelectItem key={item.id} value={item.id}>
                              {item.sku} - {item.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-24">
                      {index === 0 && <Label className="text-xs text-muted-foreground">Quantity</Label>}
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={line.quantity}
                        onChange={(e) => updateMovementLine(index, 'quantity', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    {movementForm.movement_type === 'RECEIPT' && (
                      <div className="w-28">
                        {index === 0 && <Label className="text-xs text-muted-foreground">Unit Cost</Label>}
                        <Input
                          type="number"
                          placeholder="Unit cost"
                          value={line.unit_cost}
                          onChange={(e) => updateMovementLine(index, 'unit_cost', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeMovementLine(index)}
                      disabled={movementForm.lines.length === 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={movementForm.notes}
                onChange={(e) => setMovementForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMovementDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateMovement}
              disabled={
                movementForm.lines.filter(l => l.item_id && l.quantity > 0).length === 0 ||
                ((movementForm.movement_type === 'RECEIPT' || movementForm.movement_type === 'ADJUSTMENT') && !movementForm.to_location_id) ||
                ((movementForm.movement_type === 'ISSUE' || movementForm.movement_type === 'TRANSFER') && !movementForm.from_location_id) ||
                (movementForm.movement_type === 'TRANSFER' && !movementForm.to_location_id)
              }
            >
              Create & Post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
